import { useCallback, useEffect, useState } from 'react';

import { api, type Project } from '../api/client';

interface ProjectsViewProps {
  onOpen: (project: Project) => void;
}

export function ProjectsView({ onOpen }: ProjectsViewProps): React.ReactElement {
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setProjects(await api.listProjects());
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load projects');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim() || busy) return;

    setBusy(true);
    try {
      const project = await api.createProject(name.trim(), address.trim() || undefined);
      setName('');
      setAddress('');
      onOpen(project);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not create the project');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (project: Project) => {
    const confirmed = window.confirm(
      `Delete "${project.name}" and every scenario, revision and image in it? This cannot be undone.`,
    );
    if (!confirmed) return;

    try {
      await api.deleteProject(project.id);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not delete the project');
    }
  };

  return (
    <div className="projects">
      <header className="projects-header">
        <h1>Floor plans</h1>
        <p className="muted">
          A building is a project. Each renovation idea is a scenario inside it, versioned
          separately so you can compare them.
        </p>
      </header>

      {error && <p className="error">{error}</p>}

      <form className="new-project" onSubmit={create}>
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Property name, e.g. Storgatan 4"
          aria-label="Project name"
        />
        <input
          value={address}
          onChange={(event) => setAddress(event.target.value)}
          placeholder="Address (optional)"
          aria-label="Address"
        />
        <button type="submit" disabled={!name.trim() || busy}>
          Add project
        </button>
      </form>

      {projects === null ? (
        <p className="muted">Loading…</p>
      ) : projects.length === 0 ? (
        <p className="muted">No projects yet. Add one above to start tracing a plan.</p>
      ) : (
        <ul className="project-list">
          {projects.map((project) => (
            <li key={project.id}>
              <button className="project-open" onClick={() => onOpen(project)}>
                <span className="project-name">{project.name}</span>
                {project.address && <span className="muted">{project.address}</span>}
                <span className="muted small">
                  Updated {new Date(project.updatedAt).toLocaleDateString()}
                </span>
              </button>
              <button className="danger ghost" onClick={() => remove(project)}>
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
